import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LogoDesk from '../images/LOGO-05.png';
import LogoMobile from '../images/LOGO-07.png';
import { useLocale } from '../contexts/LocaleContext';
import { FaWhatsapp } from 'react-icons/fa';

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true; // assume desktop during SSR
    return window.matchMedia('(min-width: 768px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', handler);
    else mq.addListener(handler);
    setIsDesktop(mq.matches);
    return () => {
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  const { t } = useLocale();

  const isLatinAmerica = (() => {
    try {
      const regions = new Set([
        'AR','BO','BR','CL','CO','CR','CU','DO','EC','SV','GT','HN','MX','NI','PA','PY','PE','PR','UY','VE','GF','GY','SR','BQ'
      ]);
      const langs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || '']).map(l => String(l));
      for (const l of langs) {
        const parts = l.split('-');
        if (parts.length >= 2) {
          const region = parts[1].toUpperCase();
          if (region === '419') return true;
          if (regions.has(region)) return true;
        }
      }
    } catch (err) {
      // ignore
    }
    return false;
  })();

  const latinAmericaPhoneRaw = '+543816461191';
  const latinPhoneDigits = latinAmericaPhoneRaw.replace(/[^0-9]/g, '');
  const latinWhatsappUrl = `https://api.whatsapp.com/send?phone=${latinPhoneDigits}`;

  const WhatsappIcon = (FaWhatsapp as unknown) as React.ComponentType<any>;

  return (
    <header className="sticky top-0 w-full z-50 bg-gradient-primary text-white border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center relative">
        {/* Left: logo */}
        <div className="flex items-center flex-shrink-0">
          {isDesktop ? (
            <Link to="/" aria-label="Home">
              <img src={LogoDesk} alt="Aizora" className="h-10 w-auto" />
            </Link>
          ) : (
            <Link to="/" aria-label="Home">
              <img src={LogoMobile} alt="Aizora" className="h-8 w-auto" />
            </Link>
          )}
        </div>

        {/* Center: nav links (desktop) */}
        {isDesktop && (
          <nav className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-40">
            <ul className="flex items-center gap-12">
              {((t?.nav?.links) || []).map((label: string, i: number) => (
                <li key={i}>
                  <a
                    href={i === 0 ? '/#what' : i === 1 ? '/#problem' : '/#pricing'}
                    className="block heading-font text-white py-2 hover:text-white/90 transition-colors xl:text-xl text-medium"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {/* Right: actions */}
        <div className="ml-auto flex items-center gap-4 z-50">
          {isDesktop ? (
            <button
              onClick={() => {
                if (isLatinAmerica) {
                  window.open(latinWhatsappUrl, '_blank');
                } else {
                  if (window.location.pathname !== '/') {
                    window.location.href = '/#contact';
                  } else {
                    const el = document.getElementById('contact');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                    else window.location.hash = '#contact';
                  }
                }
              }}
              className="mt-2 bg-white/10 text-white px-4 py-3 rounded-lg text-center heading-font hover:bg-white/20 transition-colors flex items-center gap-2"
              aria-label="Contact"
            >
              {isLatinAmerica && <WhatsappIcon className="w-4 h-4" aria-hidden />}
              <span>{(t?.nav?.contact) || 'Contact'}</span>
            </button>
          ) : (
            <button
              className="inline-flex items-center justify-center p-2 rounded-md focus:outline-none"
              aria-label="Menu"
              onClick={() => setOpen(prev => !prev)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d={open ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu (slide down) */}
      {open && !isDesktop && (
        <div className="bg-gradient-primary text-white border-t border-white/10">
          <div className="px-6 py-4 space-y-3">
            {((t?.nav?.links) || []).map((label: string, i: number) => (
              <a
                key={i}
                href={i === 0 ? '/#what' : i === 1 ? '/#problem' : '/#pricing'}
                className="block heading-font text-white py-2 hover:text-white/90 hover:underline transition-colors"
              >
                {label}
              </a>
            ))}

            <button
              onClick={() => {
                if (isLatinAmerica) window.open(latinWhatsappUrl, '_blank');
                else {
                  if (window.location.pathname !== '/') {
                    window.location.href = '/#contact';
                  } else {
                    const el = document.getElementById('contact');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                    else window.location.hash = '#contact';
                  }
                }
              }}
              className="mt-2 bg-white/10 text-white px-4 py-3 rounded-lg text-center heading-font hover:bg-white/20 transition-colors flex items-center gap-2"
            >
              {isLatinAmerica && <WhatsappIcon className="w-4 h-4" aria-hidden />}
              <span>{(t?.nav?.contact) || 'Contact'}</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
